import {useContext, useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import AuthLayout from '../../layout/AuthLayout.jsx';
import FormContainer from '../../components/form/FormContainer.jsx';
import Button from '../../components/Button.jsx';
import LoadingComponent from '../../components/LoadingComponent.jsx';
import FormError from '../../components/form/FormError.jsx';
import {MCPOAuthApi} from '../../api/authority/generated/index.ts';
import {GlobalContext} from '../../utils/context/GlobalContextProvider.jsx';
import {getMcpAuthorizationHandle, requestMcpConsent} from './mcpConsent.js';
import {useTranslation} from 'react-i18next';
import {hasVisibleProjectMcpAccess} from '../project/ThirdPartyServiceIntegrations/ProjectMcpGrants.jsx';

export function getEligibleMcpProjects(projects, now = new Date()) {
    return projects.filter((project) => hasVisibleProjectMcpAccess(project, true, now));
}

export function McpConsentContent({clientName, projects, selectedProjectId, onProjectChange, onApprove, onDeny, busy, t}) {
    const selectedProject = projects.find((project) => project.id === selectedProjectId) ?? null;

    return <FormContainer>
        <p className="mb-4 text-sm text-slate-600">
            {t('mcp_oauth.consent.request', {clientName, defaultValue: 'Aplikace {{clientName}} žádá o přístup pouze ke čtení reportů Datixo.'})}
        </p>
        <label className="block text-sm font-medium text-slate-700" htmlFor="mcp-project">
            {t('mcp_oauth.consent.project-label', {defaultValue: 'Projekt, ke kterému přístup povolujete'})}
        </label>
        <select
            id="mcp-project"
            className="form-select mt-2 w-full"
            value={selectedProjectId}
            onChange={(event) => onProjectChange(event.target.value)}
            disabled={busy}
        >
            <option value="">{t('mcp_oauth.consent.select-project', {defaultValue: 'Vyberte projekt'})}</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
        {projects.length === 0 && <p role="status" className="mt-3 text-sm text-slate-600">
            {t('mcp_oauth.consent.no-eligible-projects', {defaultValue: 'Nemáte projekt s aktivním MCP přístupem.'})}
        </p>}
        {selectedProject && <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
            {t('mcp_oauth.consent.project-scope', {
                projectName: selectedProject.name,
                defaultValue: 'AI klient získá přístup jen k projektu {{projectName}}. Projekt nebude možné tokenem změnit.',
            })}
        </p>}
        <p className="mt-4 text-xs text-slate-500">
            {t('mcp_oauth.consent.read-only-notice', {defaultValue: 'Přístup dovoluje pouze předem definované read-only reporty. Neumožňuje zápis ani přímý přístup do databáze.'})}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
            <Button variant="black" disabled={busy || selectedProject === null} onClick={onApprove}>
                {t('mcp_oauth.consent.approve', {defaultValue: 'Povolit'})}
            </Button>
            <Button variant="outline-red" disabled={busy} onClick={onDeny}>
                {t('mcp_oauth.consent.deny', {defaultValue: 'Zamítnout'})}
            </Button>
        </div>
    </FormContainer>;
}

export default function McpAuthorizationConsent() {
    const {t} = useTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const location = useLocation();
    const handle = useMemo(() => getMcpAuthorizationHandle(location.search), [location.search]);
    const [detailState, setDetailState] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const token = context.currentIdentity?.token ?? null;
    const detail = detailState?.handle === handle ? detailState.value : null;

    useEffect(() => {
        if (!token || handle === null) return;
        let active = true;
        setDetailState(null);
        setSelectedProjectId('');
        setError(null);
        setBusy(true);
        requestMcpConsent({api: new MCPOAuthApi(getAuthorityConfiguration()), handle})
            .then((value) => { if (active) setDetailState({handle, value}); })
            .catch((requestError) => { if (active) setError(requestError); })
            .finally(() => { if (active) setBusy(false); });
        return () => { active = false; };
    }, [getAuthorityConfiguration, handle, refreshKey, token]);

    if (handle === null) {
        return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})}>
            <FormError error={new Error(t('mcp_oauth.consent.invalid-request', {defaultValue: 'Neplatný autorizační požadavek.'}))}/>
        </AuthLayout>;
    }

    if (!token) {
        const returnTo = `${location.pathname}${location.search}`;
        return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})}>
            <FormContainer>
                <p className="mb-4 text-sm text-slate-600">
                    {t('mcp_oauth.consent.sign-in-required', {defaultValue: 'Pro výběr projektu se nejprve přihlaste do Datixo.'})}
                </p>
                <Link className="btn bg-gray-900 text-gray-100" to={`/sign/in?returnTo=${encodeURIComponent(returnTo)}`}>
                    {t('mcp_oauth.consent.sign-in', {defaultValue: 'Přihlásit se'})}
                </Link>
            </FormContainer>
        </AuthLayout>;
    }

    const finish = async (action) => {
        setBusy(true);
        setError(null);
        try {
            const result = await requestMcpConsent({
                api: new MCPOAuthApi(getAuthorityConfiguration()),
                handle,
                action,
                projectId: action === 'approve' ? selectedProjectId : null,
            });
            window.location.assign(result.redirectUri);
        } catch (requestError) {
            setError(requestError);
            setBusy(false);
        }
    };

    const eligibleProjects = getEligibleMcpProjects(context.currentUserProjects ?? []);

    return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})}>
        {busy && detail === null && <p className="sr-only" role="status" aria-live="polite">
            {t('mcp_oauth.consent.loading', {defaultValue: 'Načítám autorizační požadavek…'})}
        </p>}
        <LoadingComponent loading={busy && detail === null}>
            {error && <>
                <FormError error={error}/>
                {detail === null && <FormContainer>
                    <p className="mb-4 text-sm text-slate-600">
                        {t('mcp_oauth.consent.load-failed', {defaultValue: 'Autorizační požadavek se nepodařilo načíst.'})}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button variant="outline-primary" disabled={busy} onClick={() => setRefreshKey((key) => key + 1)}>
                            {t('mcp_oauth.consent.retry', {defaultValue: 'Zkusit znovu'})}
                        </Button>
                        <Button variant="outline-red" disabled={busy} onClick={() => void finish('deny')}>
                            {t('mcp_oauth.consent.deny', {defaultValue: 'Zamítnout'})}
                        </Button>
                        <Link className="btn border border-gray-300 bg-white text-gray-700" to="/">
                            {t('mcp_oauth.consent.back', {defaultValue: 'Zpět do Datixo'})}
                        </Link>
                    </div>
                </FormContainer>}
            </>}
            {detail && <McpConsentContent
                clientName={detail.clientName}
                projects={eligibleProjects}
                selectedProjectId={selectedProjectId}
                onProjectChange={setSelectedProjectId}
                onApprove={() => void finish('approve')}
                onDeny={() => void finish('deny')}
                busy={busy}
                t={t}
            />}
        </LoadingComponent>
    </AuthLayout>;
}
